import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useProposalStore } from '../../stores/proposalStore';
import { proposalGenerator } from '../../lib/api/ai/proposal';
import { samGovApi } from '../../lib/api/samGov';
import { Wand2, Save, ArrowLeft, Loader2, FileText } from 'lucide-react';
import { toast } from 'react-hot-toast';

export function ProposalEditor() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { proposals, updateProposal } = useProposalStore();
  const [proposal, setProposal] = useState(proposals.find(p => p.id === id));
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [contractData, setContractData] = useState(null);

  useEffect(() => {
    if (!proposal) {
      toast.error('Proposal not found');
      navigate('/proposals');
      return;
    }

    // Fetch contract data from SAM.gov if contractId exists
    const fetchContractData = async () => {
      if (proposal.contractId) {
        try {
          const response = await samGovApi.fetchContracts({ 
            noticeId: proposal.contractId,
            limit: 1
          });
          
          if (response && response.length > 0) {
            setContractData(response[0]);
          } else {
            throw new Error('Contract not found');
          }
        } catch (error) {
          console.error('Failed to fetch contract data:', error);
          toast.error('Failed to load contract details');
        }
      }
    };

    fetchContractData();
  }, [proposal, navigate]);

  const handleSectionChange = (sectionId: string, content: string) => {
    if (!proposal) return;

    const updatedProposal = {
      ...proposal,
      sections: proposal.sections.map(section =>
        section.id === sectionId ? { ...section, content } : section
      ),
    };
    setProposal(updatedProposal);
  };

  const handleSave = async () => {
    if (!proposal) return;
    setLoading(true);
    try {
      await updateProposal(proposal.id, proposal);
      toast.success('Proposal saved successfully');
    } catch (error) {
      toast.error('Failed to save proposal');
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateContent = async (sectionId: string) => {
    if (!proposal) return;
    setGenerating(true);

    try {
      const section = proposal.sections.find(s => s.id === sectionId);
      if (!section) throw new Error('Section not found');

      const improvedSection = await proposalGenerator.improveSection(section);
      const updatedProposal = {
        ...proposal,
        sections: proposal.sections.map(s =>
          s.id === sectionId ? improvedSection : s
        ),
      };

      setProposal(updatedProposal);
      await updateProposal(proposal.id, updatedProposal);
      toast.success('Content generated successfully');
    } catch (error) {
      toast.error('Failed to generate content');
    } finally {
      setGenerating(false);
    }
  };

  if (!proposal) return null;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <button
            onClick={() => navigate('/proposals')}
            className="rounded-full p-2 text-gray-400 hover:bg-gray-100 hover:text-gray-500"
          >
            <ArrowLeft className="h-5 w-5" />
          </button>
          <div>
            <h1 className="text-2xl font-semibold text-gray-900">{proposal.title}</h1>
            <p className="mt-1 text-sm text-gray-500">
              Due: {new Date(proposal.deadline).toLocaleDateString()}
            </p>
          </div>
        </div>
        <button
          onClick={handleSave}
          disabled={loading}
          className="inline-flex items-center rounded-lg bg-purple-600 px-4 py-2 text-sm font-medium text-white hover:bg-purple-700"
        >
          {loading ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Saving...
            </>
          ) : (
            <>
              <Save className="mr-2 h-4 w-4" />
              Save Changes
            </>
          )}
        </button>
      </div>

      {/* Contract Details */}
      {contractData && (
        <div className="rounded-lg bg-gray-50 p-4">
          <h2 className="text-sm font-medium text-gray-700">Contract Details</h2>
          <div className="mt-2 grid grid-cols-2 gap-4">
            <div>
              <p className="text-sm text-gray-500">Agency</p>
              <p className="text-sm font-medium text-gray-900">{contractData.agency}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500">Value</p>
              <p className="text-sm font-medium text-gray-900">
                {contractData.value
                  ? new Intl.NumberFormat('en-US', {
                      style: 'currency',
                      currency: 'USD',
                      minimumFractionDigits: 0,
                    }).format(contractData.value)
                  : 'Not specified'}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Sections */}
      <div className="space-y-6">
        {proposal.sections.map((section) => (
          <div
            key={section.id}
            className="rounded-lg border border-gray-200 bg-white p-6"
          >
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-medium text-gray-900">{section.title}</h2>
              <button
                onClick={() => handleGenerateContent(section.id)}
                disabled={generating}
                className="inline-flex items-center rounded-lg bg-purple-600 px-4 py-2 text-sm font-medium text-white hover:bg-purple-700"
              >
                {generating ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Generating...
                  </>
                ) : (
                  <>
                    <Wand2 className="mr-2 h-4 w-4" />
                    Generate Content
                  </>
                )}
              </button>
            </div>

            <div className="mt-4">
              <textarea
                value={section.content}
                onChange={(e) => handleSectionChange(section.id, e.target.value)}
                rows={10}
                className="w-full rounded-lg border border-gray-300 p-4 focus:border-purple-500 focus:outline-none focus:ring-1 focus:ring-purple-500"
                placeholder={`Enter ${section.title.toLowerCase()} content...`}
              />
            </div>

            {/* Compliance Checks */}
            {section.compliance.length > 0 && (
              <div className="mt-4 space-y-2">
                <h3 className="text-sm font-medium text-gray-900">Compliance Checks</h3>
                {section.compliance.map((check, index) => (
                  <div
                    key={index}
                    className={`rounded-lg p-3 ${
                      check.status === 'passed'
                        ? 'bg-green-50 text-green-700'
                        : check.status === 'warning'
                        ? 'bg-yellow-50 text-yellow-700'
                        : 'bg-red-50 text-red-700'
                    }`}
                  >
                    <p className="text-sm">{check.message}</p>
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}